import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import { Footer, FooterButtons, FooterError } from '@/design_system/Footer';
import { useValidateServiceChoice } from '@/models/article';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';

import { articleDetailsErrors } from '../../ArticleInformationFormDrawer';

import { ArticleActionProps } from './ArticleActions';

export const ChooseArticleServiceAction = ({ onActionDone }: ArticleActionProps) => {
  const { t } = useLingui();

  const { view: requestView } = useRequestContext();
  const { article, view, state, hasError } = useArticleContext();

  const [apiError, setApiError] = useState<string | null>(null);

  const {
    mutateAsync: validateServiceChoice,
    isPending: isPendingValidateServiceChoice,
    isSuccess: isSuccessValidateServiceChoice,
  } = useValidateServiceChoice({
    articleId: article.id,
  });

  const handleValidateServiceChoice = async () => {
    const { hasError } = state.errors.check({
      details: articleDetailsErrors(view),
      customs: view.services.dispatch.shown,
      warranty:
        view.warranty.shown && view.warranty.editable
          ? {
              missingPurchaseDate:
                view.warranty.fields.purchaseDate.shown &&
                view.warranty.fields.purchaseDate.required,
              invalidPurchaseDate: view.warranty.fields.purchaseDate.shown,
              missingPurchaseLocation:
                view.warranty.fields.purchaseLocation.shown &&
                view.warranty.fields.purchaseLocation.required,
              missingPurchaseCountry:
                view.warranty.fields.purchaseCountry.shown &&
                view.warranty.fields.purchaseCountry.required,
              missingPurchaseChannel:
                view.warranty.fields.purchaseChannel.shown &&
                view.warranty.fields.purchaseChannel.required,
              missingOrderNumber:
                view.warranty.fields.orderNumber.shown && view.warranty.fields.orderNumber.required,
              missingProofOfPurchase:
                view.warranty.fields.proofOfPurchase.shown &&
                view.warranty.fields.proofOfPurchase.required,
              missingProofOfPurchaseOrOrderNumber:
                view.warranty.fields.orderNumber.shown &&
                view.warranty.fields.proofOfPurchase.shown &&
                view.warranty.fields.proofOfPurchaseOrOrderNumber.required,
              missingProofOfAuthenticity:
                view.warranty.fields.proofOfAuthenticity.shown &&
                view.warranty.fields.proofOfAuthenticity.required,
              missingOverrideReason: view.warranty.fields.warrantyOverrideReason.required,
            }
          : undefined,
      defects: view.defects.shown
        ? {
            noDefects: view.defects.required,
            defects: {
              missingDescription: view.defects.fields.description.editable,
              missingPhotos:
                view.defects.fields.photo.shown &&
                view.defects.fields.photo.editable &&
                view.defects.fields.photo.required,
              missingContext:
                view.defects.fields.context.shown &&
                view.defects.fields.context.editable &&
                view.defects.fields.context.required,
              missingActions:
                view.defects.fields.toBeRepaired.shown &&
                view.defects.fields.toBeRepaired.editable &&
                view.services.actions.shown,
              missingExceptionalCoverageReason:
                view.defects.fields.brandResponsibility.exceptionalCoverage.enabled &&
                view.defects.fields.brandResponsibility.exceptionalCoverage.toggle.editable,
            },
          }
        : undefined,
      services: {
        choice: view.services.choice.shown
          ? {
              noChoice: true,
            }
          : undefined,
        actions: view.services.actions.shown
          ? {
              noActions: view.services.actions.canAddAction,
              actions: {
                missingDescription: view.services.actions.fields.description.editable,
                missingPhotos:
                  view.services.actions.fields.photo.shown &&
                  view.services.actions.fields.photo.editable &&
                  view.services.actions.fields.photo.required,
                missingRefashionPhoto:
                  view.services.actions.fields.photo.shown &&
                  view.services.actions.fields.photo.editable,
                missingContext:
                  view.services.actions.fields.context.shown &&
                  view.services.actions.fields.context.editable &&
                  view.services.actions.fields.context.required,
                missingOrganizationPrice:
                  requestView.organizationPrice.shown &&
                  view.services.actions.fields.organizationPrice.editable &&
                  view.services.actions.fields.organizationPrice.required,
                organizationPriceUpdateInProgress:
                  requestView.organizationPrice.shown &&
                  view.services.actions.fields.organizationPrice.editable,
                spareParts: view.services.actions.fields.spareParts.shown
                  ? {
                      quantityIsNull: view.services.actions.fields.spareParts.quantity.editable,
                      missingOrganizationPrice:
                        requestView.organizationPrice.shown &&
                        view.services.actions.fields.spareParts.organizationPrice.editable &&
                        view.services.actions.fields.spareParts.organizationPrice.required,
                      organizationPriceUpdateInProgress:
                        requestView.organizationPrice.shown &&
                        view.services.actions.fields.spareParts.organizationPrice.editable,
                    }
                  : undefined,
                additionalInfo: {
                  missingValue: view.services.actions.fields.additionalInfo.editable,
                },
              },
            }
          : undefined,
        dispatch: view.services.dispatch.shown
          ? {
              noWorkshopSelected: true,
            }
          : undefined,
        exchange: view.services.exchange.shown
          ? {
              missingReplacementProduct: view.services.exchange.replacementProduct.editable,
              missingReplacementProductColor: view.details.color.shown,
              missingReplacementProductSize: view.details.size.shown,
              missingStockLocation: view.services.exchange.stockLocation.editable,
              missingOrganizationPrice:
                requestView.organizationPrice.shown &&
                view.services.exchange.organizationPrice.editable &&
                view.services.exchange.organizationPrice.required,
              organizationPriceUpdateInProgress:
                requestView.organizationPrice.shown &&
                view.services.exchange.organizationPrice.editable,
            }
          : undefined,
      },
      thresholds: {
        organization:
          view.prices.thresholds.organization.shown && view.prices.thresholds.organization.editable,
      },
    });

    setApiError(null);

    if (!hasError) {
      try {
        await validateServiceChoice();
        onActionDone();
      } catch (err: any) {
        console.error(err);
        setApiError(
          (err.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
        );
      }
    }
  };

  return (
    <Footer>
      {(hasError || apiError) && (
        <FooterError>
          {hasError ? (
            <Trans id="article.actions.choose_article_service.errors">
              Please fill the missing information in order to validate the diagnosis
            </Trans>
          ) : (
            apiError
          )}
        </FooterError>
      )}
      <FooterButtons>
        <Button
          variant="primary"
          size="medium"
          onPress={handleValidateServiceChoice}
          isLoading={isPendingValidateServiceChoice || isSuccessValidateServiceChoice}
          className="text-no-wrap"
        >
          <Trans id="article.actions.choose_article_service.validate">Complete diagnosis</Trans>
        </Button>
      </FooterButtons>
    </Footer>
  );
};
